// Google Identity Services (GSI) — frontend-only Google Sign-In helper.
// Uses the OAuth2 access-token flow (popup), then fetches the user's profile
// from Google's userinfo endpoint. No backend required.

const SCOPE = "openid email profile";

const waitForGoogle = () =>
  new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (window.google?.accounts?.oauth2) return resolve(window.google);
      if (Date.now() - start > 8000) return reject(new Error("Google Sign-In script failed to load"));
      setTimeout(tick, 80);
    };
    tick();
  });

export const getGoogleClientId = () =>
  (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim();

export const isGoogleConfigured = () => Boolean(getGoogleClientId());

/**
 * Triggers a Google sign-in popup. Resolves with the Google profile object:
 *   { sub, email, name, given_name, family_name, picture }
 */
export const signInWithGooglePopup = async () => {
  const clientId = getGoogleClientId();
  if (!clientId) throw new Error("Google sign-in isn't configured yet. Set REACT_APP_GOOGLE_CLIENT_ID.");

  const google = await waitForGoogle();

  return new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      prompt: "",
      callback: async (response) => {
        if (response.error) return reject(new Error(response.error_description || response.error));
        if (!response.access_token) return reject(new Error("No access token returned from Google"));
        try {
          const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
            headers: { Authorization: `Bearer ${response.access_token}` },
          });
          if (!res.ok) throw new Error(`Userinfo fetch failed (${res.status})`);
          const profile = await res.json();
          resolve(profile);
        } catch (err) {
          reject(err);
        }
      },
      error_callback: (err) => reject(new Error(err?.message || "Google sign-in was cancelled")),
    });
    client.requestAccessToken();
  });
};
